/**
 * Single source of truth for the `choose_plan_order` A/B/C test (HL-12001).
 *
 * Weights come from the `choose_plan_order_ab_test_weights` variable (JSON per arm, e.g.
 * {"A":0.34,"B":0.33,"C":0.33}; B and C at 0 = test off), returned with the plan matrix. The
 * variant is assigned once per browser by ABTestManager 
 *
 * Analytics (ticket scope only): `choose_plan_order_variant` goes on `choose_plan_viewed`, on the
 * Mixpanel user profile, and on the server-side "Paid Order" event. That last one
 * (offline-pixels-consumer MixpanelEventSender) spreads the experiments saved on the cart, so the
 * V3 page sends the arm as `experiments: { choose_plan_order_variant }` on its
 * choose-treatment-free-dosage POST.
 */

import { ABTestManager } from "./ABTestManager";
import { MainTracker } from "../PixelTrackers/MainTracker";

export type ChoosePlanOrderVariant = "A" | "B" | "C";
export type ChoosePlanSection = "trust" | "dosage" | "one_time" | "prescription_only";

export const CHOOSE_PLAN_ORDER_TEST_NAME = "choose_plan_order";
export const CHOOSE_PLAN_ORDER_VARIANTS: readonly ChoosePlanOrderVariant[] = ["A", "B", "C"];
export const CHOOSE_PLAN_ORDER_PROPERTY = "choose_plan_order_variant";

/** Page sections after the Monthly card, top to bottom, per arm. */
export const CHOOSE_PLAN_SECTION_ORDER: Record<ChoosePlanOrderVariant, ChoosePlanSection[]> = {
  A: ["trust", "dosage", "one_time", "prescription_only"],
  B: ["one_time", "trust", "dosage", "prescription_only"],
  C: ["one_time", "prescription_only", "trust", "dosage"],
};

/** Where the "Not ready to subscribe?" link under the Monthly card scrolls; null = no link. */
export const CHOOSE_PLAN_LINK_TARGET: Record<ChoosePlanOrderVariant, ChoosePlanSection | null> = {
  A: "one_time",
  B: "prescription_only",
  C: null,
};

const STORAGE_KEY = `abtest_${CHOOSE_PLAN_ORDER_TEST_NAME}`;

const isVariant = (value: unknown): value is ChoosePlanOrderVariant =>
  typeof value === "string" && (CHOOSE_PLAN_ORDER_VARIANTS as readonly string[]).includes(value);

/**
 * Parse the raw variable value into normalised [A, B, C] weights. Anything unusable (missing,
 * invalid JSON, negative/NaN, all zero) collapses to control so a broken variable never reorders
 * the page.
 */
export const parseChoosePlanOrderWeights = (raw: unknown): number[] => {
  const control = [1, 0, 0];
  if (raw === null || raw === undefined || raw === "") return control;
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return control;
    }
  }
  if (!parsed || typeof parsed !== "object") return control;
  const weights = CHOOSE_PLAN_ORDER_VARIANTS.map((arm) => {
    const w = Number((parsed as Record<string, unknown>)[arm]);
    return Number.isFinite(w) && w > 0 ? w : 0;
  });
  const total = weights.reduce((sum, w) => sum + w, 0);
  if (total <= 0) return control;
  return weights.map((w) => w / total);
};

/**
 * Assign (or load the sticky) variant and put it on Mixpanel. Call once per page load from the
 * V3 Choose Plan page after the plans arrive. To force an arm for QA, set the localStorage key
 * `abtest_choose_plan_order` to A, B or C.
 */
export const assignChoosePlanOrderVariant = (rawWeights: unknown): ChoosePlanOrderVariant => {
  const raw = new ABTestManager({
    testName: CHOOSE_PLAN_ORDER_TEST_NAME,
    variants: [...CHOOSE_PLAN_ORDER_VARIANTS],
    weights: parseChoosePlanOrderWeights(rawWeights),
  }).getVariant();
  const variant: ChoosePlanOrderVariant = isVariant(raw) ? raw : "A";
  // AC-15: the arm on the Mixpanel user profile.
  MainTracker.people.set({ [CHOOSE_PLAN_ORDER_PROPERTY]: variant });
  // HL-12122: and as the lowercase `choose_plan_variant` super-property on every later event.
  MainTracker.superProperties({ choose_plan_variant: variant.toLowerCase() });
  return variant;
};

/** Sticky read for places without the server weights. "A" when never assigned. */
export const getChoosePlanOrderVariant = (): ChoosePlanOrderVariant => {
  if (typeof window === "undefined") return "A";
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isVariant(stored) ? stored : "A";
  } catch {
    return "A";
  }
};

/** Sticky arm, or null when this browser was never assigned (no default, unlike getChoosePlanOrderVariant). */
export const getAssignedChoosePlanOrderVariant = (): ChoosePlanOrderVariant | null => {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isVariant(stored) ? stored : null;
  } catch {
    return null;
  }
};
